import Bugsnag from '@bugsnag/js';
import { SUPPORTED_OVERFLOW_LOCALES } from '@99designs/design-services-common';
import { useExpertServiceLazyQuery } from '../../../../graph';
import { useBriefContext } from '../../../BriefContext';
import { useSelectProductWithCacheUpdate } from '../ClientConfiguredFields/ProductDropdown/useSelectProductMutation';
import { useUpdateField } from '../useUpdateField';
import { useBriefSetGridChoiceMutation } from './briefSetGridChoice.generated';

const DESIGN_LIVE_SERVICE_MPV_ID = 'designLive';
const CARE_DESIGN_SERVICE_MPV_ID = 'careDesign';
const OTHER_DESIGN_SERVICE_MPV_ID = 'otherDesign';

export function useUpdateGridChoiceField(
    key: string,
    locale: string,
    briefId?: string,
    initialValue?: string | null
) {
    const { selectProduct } = useSelectProductWithCacheUpdate();
    const { serviceProductMpvId, expertService } = useBriefContext();
    const printProductMpvId = expertService?.printProduct?.mpvId;
    const [fetchExpertService] = useExpertServiceLazyQuery();

    const [setGridChoice] = useBriefSetGridChoiceMutation({
        onCompleted: async (briefData) => {
            if (key !== 'requestType') {
                return;
            }
            const field = briefData.setField.dynamicFields.find((field) => field.id === key);
            if (!field) {
                return;
            }
            if (field.__typename !== 'GridChoiceField') {
                throw new Error(
                    "Field is not a GridChoiceField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                );
            }
            if (briefData.setField.correlationId && serviceProductMpvId) {
                const resolvedMpvId = await resolveServiceProductMpvId(
                    field.choiceValue || '',
                    serviceProductMpvId,
                    printProductMpvId,
                    fetchExpertService,
                    locale
                );

                selectProduct({
                    variables: {
                        requestId: briefData.setField.correlationId,
                        input: {
                            serviceProductMpvId: resolvedMpvId,
                            printProductMpvId: [],
                        },
                    },
                });
            }
        },
    });
    return useUpdateField<string>({ key, setField: setGridChoice, briefId, initialValue });
}

export async function resolveServiceProductMpvId(
    fieldChoiceValue: string,
    existingServiceProductMpvId: string,
    printProductMpvId: string | undefined,
    fetchExpertService: ReturnType<typeof useExpertServiceLazyQuery>[0],
    locale: string
): Promise<string> {
    /**
     * Fallback to legacy DesignLive mpvId
     * because the new service products don't support DesignLive yet.
     */
    if (fieldChoiceValue.toLowerCase() === DESIGN_LIVE_SERVICE_MPV_ID.toLowerCase()) {
        return DESIGN_LIVE_SERVICE_MPV_ID;
    }

    if (!printProductMpvId) {
        return CARE_DESIGN_SERVICE_MPV_ID;
    }

    /**
     * If the print product mpvId is present, we need to fetch the expert service to determine which service product mpvId to use.
     * This is because some the graph has some logic to prioritize certain service products based on the print product mpvId.
     */
    const { data, error } = await fetchExpertService({
        variables: { mpvId: printProductMpvId },
    });
    if (error) {
        Bugsnag.notify(
            new Error(
                `Error fetching expert service for print product mpvId ${printProductMpvId}: ${error.message}`
            )
        );
    }

    const resolvedMpvId = data?.expertService?.serviceProduct?.mpvId;

    if (resolvedMpvId?.toLowerCase() === OTHER_DESIGN_SERVICE_MPV_ID.toLowerCase()) {
        return OTHER_DESIGN_SERVICE_MPV_ID;
    }

    const isOverflowEligible = data?.expertService?.isOverflowEligible ?? false;
    const isOverflowLocale = SUPPORTED_OVERFLOW_LOCALES.find(
        (supportedLocale) => supportedLocale.toLowerCase() === locale.toLowerCase()
    );

    if (isOverflowEligible && isOverflowLocale && resolvedMpvId) {
        return resolvedMpvId;
    }

    return CARE_DESIGN_SERVICE_MPV_ID;
}
